"use client";

import { useState, useEffect } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Button } from "@/shadcn-components/ui/button";
import { Badge } from "@/shadcn-components/ui/badge";
import { CalendarIcon, CheckIcon } from "lucide-react";

// Importy komponentów
import DatePickerDrawer from "./DatePickerDrawer";
import {
  DateFilter,
  useFilterStore,
  DATE_FILTER_OPTIONS,
} from "@/vybe-trader-client/trade-history/stores/useFilterStore";
import HistorySymbolSelectionScreen from "./HistorySymbolSelectionScreen";

interface HistoryFilterScreenProps {
  onBack: () => void;
}

export default function HistoryFilterScreen({
  onBack,
}: HistoryFilterScreenProps) {
  const [showSymbolSelection, setShowSymbolSelection] = useState(false);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [datePickerType, setDatePickerType] = useState<"start" | "end">(
    "start"
  );
  const [currentMonth, setCurrentMonth] = useState(() => new Date());

  // Handle escape key to close drawer
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showCustomDatePicker) {
        setShowCustomDatePicker(false);
      }
    };
    
    if (showCustomDatePicker) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showCustomDatePicker]);

  // Pobierz dane ze store
  const {
    selectedSymbol,
    dateFilter,
    customDateFrom,
    customDateTo,
    setSelectedSymbol,
    setDateFilter,
    setCustomDateRange,
  } = useFilterStore();

  // Lokalne stany dla custom dat (przed zatwierdzeniem)
  const [localCustomFrom, setLocalCustomFrom] = useState(customDateFrom);
  const [localCustomTo, setLocalCustomTo] = useState(customDateTo);

  // Synchronizuj lokalne stany z wartościami ze store
  useEffect(() => {
    if (customDateFrom && customDateTo) {
      setLocalCustomFrom(customDateFrom);
      setLocalCustomTo(customDateTo);
    }
  }, [customDateFrom, customDateTo]);

  // Konwersja string DD.MM na Date
  const stringToDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const [day, month] = dateStr.split(".");
    if (!day || !month) return null;
    const year = new Date().getFullYear();
    return new Date(year, parseInt(month) - 1, parseInt(day));
  };

  // Konwersja Date na string DD.MM
  const dateToString = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${day}.${month}`;
  };

  const handleDateFilterSelect = (filter: DateFilter) => {
    setDateFilter(filter);

    // Jeśli wybrano Custom
    if (filter === "custom") {
      // Sprawdź czy są już zapisane custom daty w store
      if (!customDateFrom || !customDateTo) {
        // Tylko jeśli nie ma zapisanych dat, ustaw domyślne
        const today = new Date();
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

        const todayStr = dateToString(today);
        const twoWeeksAgoStr = dateToString(twoWeeksAgo);

        setLocalCustomFrom(twoWeeksAgoStr);
        setLocalCustomTo(todayStr);
        setCustomDateRange(twoWeeksAgoStr, todayStr);
      } else {
        // Jeśli są zapisane daty, użyj ich
        setLocalCustomFrom(customDateFrom);
        setLocalCustomTo(customDateTo);
      }
      // NIE otwieraj modalu automatycznie
    }
  };

  const handleSymbolSelect = (symbol: string) => {
    setSelectedSymbol(symbol);
    setShowSymbolSelection(false);
  };

  const formatDateDisplay = (dateStr: string): string => {
    const date = stringToDate(dateStr);
    if (!date) return "Not set";

    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleDateSelect = (dateStr: string) => {
    if (datePickerType === "start") {
      setLocalCustomFrom(dateStr);
      // Jeśli nowa data startowa jest po dacie końcowej, ustaw też końcową
      const startDate = stringToDate(dateStr);
      const endDate = stringToDate(localCustomTo);
      if (endDate && startDate && startDate > endDate) {
        setLocalCustomTo(dateStr);
      }
      // Zapisz do store od razu
      setCustomDateRange(dateStr, localCustomTo);
      // NIE przełączaj automatycznie na end date - pozwól użytkownikowi wybrać
    } else {
      setLocalCustomTo(dateStr);
      // Zapisz do store
      setCustomDateRange(localCustomFrom, dateStr);
      // NIE zamykaj drawer automatycznie
    }
  };

  const handleStartDateClick = () => {
    // Jeśli nie ma jeszcze ustawionych dat, ustaw domyślne
    if (!localCustomFrom || !localCustomTo) {
      const today = new Date();
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

      const todayStr = dateToString(today);
      const twoWeeksAgoStr = dateToString(twoWeeksAgo);

      setLocalCustomFrom(twoWeeksAgoStr);
      setLocalCustomTo(todayStr);
    }
    setDatePickerType("start");
    setShowCustomDatePicker(true);
  };

  const handleEndDateClick = () => {
    // Jeśli nie ma jeszcze ustawionych dat, ustaw domyślne
    if (!localCustomFrom || !localCustomTo) {
      const today = new Date();
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

      const todayStr = dateToString(today);
      const twoWeeksAgoStr = dateToString(twoWeeksAgo);

      setLocalCustomFrom(twoWeeksAgoStr);
      setLocalCustomTo(todayStr);
    }
    setDatePickerType("end");
    setShowCustomDatePicker(true);
  };

  if (showSymbolSelection) {
    return (
      <HistorySymbolSelectionScreen
        onBack={() => setShowSymbolSelection(false)}
        onSelectSymbol={handleSymbolSelect}
        selectedSymbol={selectedSymbol}
      />
    );
  }

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header with fixed height according to guidelines */}
      <div className="flex items-center justify-between px-3 h-11 bg-background border-b border-border">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-9 w-9">
          <FaChevronLeft className="h-4 w-4 text-blue-400" />
        </Button>
        <h1 className="text-sm font-medium text-foreground">Filters</h1>
        {/* Empty div for spacing balance */}
        <div className="w-9"></div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Symbol Section using Card */}
        <div className="px-3 pt-4 pb-3">
          <Card>
            <CardContent className="p-0">
              <Button
                variant="ghost"
                onClick={() => setShowSymbolSelection(true)}
                className="w-full h-auto p-3 justify-between hover:bg-muted/50"
              >
                <span className="text-sm font-medium">Symbol:</span>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">
                    {selectedSymbol === 'allsymbols' ? 'All symbols' : selectedSymbol}
                  </span>
                  <Badge variant="outline" className="ml-2">
                    {selectedSymbol === 'allsymbols' ? 'All' : selectedSymbol}
                  </Badge>
                </div>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Date Filter Selection using Card */}
        {!showCustomDatePicker && (
          <div className="px-3 pb-3">
            <Card>
              <CardContent className="p-0">
                {DATE_FILTER_OPTIONS.map((option, index) => (
                  <Button
                    key={option.value}
                    variant="ghost"
                    onClick={() => handleDateFilterSelect(option.value)}
                    className={`w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-none
                      ${index !== DATE_FILTER_OPTIONS.length - 1 ? "border-b border-border" : ""}
                      ${index === 0 ? "rounded-t-lg" : ""}
                      ${index === DATE_FILTER_OPTIONS.length - 1 ? "rounded-b-lg" : ""}
                    `}
                  >
                    <span className="text-sm font-medium">{option.label}</span>
                    {dateFilter === option.value && (
                      <CheckIcon className="h-5 w-5 text-blue-400" />
                    )}
                  </Button>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Date Picker Drawer */}
        <DatePickerDrawer
          isOpen={showCustomDatePicker}
          onClose={() => setShowCustomDatePicker(false)}
          currentMonth={currentMonth}
          onMonthChange={setCurrentMonth}
          selectedStartDate={localCustomFrom}
          selectedEndDate={localCustomTo}
          datePickerType={datePickerType}
          onDateSelect={handleDateSelect}
          onTypeChange={setDatePickerType}
        />

        {/* Custom Date Display using Cards */}
        {dateFilter === "custom" && !showCustomDatePicker && (
          <div className="px-3 pb-3 space-y-2">
            <Card className="transition-all duration-200 hover:shadow-md">
              <CardContent className="p-0">
                <Button
                  variant="ghost"
                  onClick={handleStartDateClick}
                  className="w-full h-auto p-3 justify-between hover:bg-muted/50 transition-all duration-200 group"
                >
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    <span className="text-sm font-medium">Start Date:</span>
                  </div>
                  <span className="text-primary text-sm font-medium transition-all duration-200 group-hover:scale-105">
                    {customDateFrom ? formatDateDisplay(customDateFrom) : "Select date"}
                  </span>
                </Button>
              </CardContent>
            </Card>

            <Card className="transition-all duration-200 hover:shadow-md">
              <CardContent className="p-0">
                <Button
                  variant="ghost"
                  onClick={handleEndDateClick}
                  className="w-full h-auto p-3 justify-between hover:bg-muted/50 transition-all duration-200 group"
                >
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    <span className="text-sm font-medium">End Date:</span>
                  </div>
                  <span className="text-primary text-sm font-medium transition-all duration-200 group-hover:scale-105">
                    {customDateTo ? formatDateDisplay(customDateTo) : "Select date"}
                  </span>
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bottom spacing */}
        <div className="h-20" />
      </div>
    </div>
  );
}